import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  fetchScenarioTemplates,
  type ScenarioTemplateSummary,
} from "@/src/api/scenario-templates";

type ScenarioStepProps = {
  selectedTemplateId: string | null;
  onNext: (templateId: string) => void;
  onSkip: () => void;
  onBack: () => void;
};

/**
 * Display-only badge metadata keyed by template id. The kernel registry
 * carries the canonical id/name/description/ruleset; badges are UI hints
 * and live wizard-side. Templates without overrides render without
 * badges (graceful degradation — new ScenarioTemplates surface in the
 * wizard immediately on registry add; their badges land when display
 * metadata is curated here).
 */
const BADGE_OVERRIDES: Record<string, readonly string[]> = {
  tavern: ["3 NPCs", "1 Hostile", "2 Objectives"],
  whisperwood: ["7 Hexes", "12 Passages", "Hex Mode"],
  "sunken-crypt": ["6 Rooms", "2 Levels", "Dungeon Mode"],
  greycross: ["6 PoIs", "City + Wilderness", "Mode-Crossing Gate"],
};

const RULESET_LABELS: Record<string, string> = {
  dnd5e: "D&D 5e",
};

const renderRuleset = (ruleset: string): string =>
  RULESET_LABELS[ruleset] ?? ruleset;

export function ScenarioStep({
  selectedTemplateId,
  onNext,
  onSkip,
  onBack,
}: ScenarioStepProps) {
  const [templates, setTemplates] = useState<ScenarioTemplateSummary[] | null>(
    null
  );
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [localSelection, setLocalSelection] = useState<string | null>(
    selectedTemplateId
  );

  useEffect(() => {
    let cancelled = false;
    fetchScenarioTemplates()
      .then((list) => {
        if (cancelled) {
          return;
        }
        setTemplates(list);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setFetchError(
          err instanceof Error ? err.message : "Failed to load scenarios"
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4">
      <div className="text-center">
        <h2 className="font-semibold text-xl">Choose your starting scenario</h2>
        <p className="mt-1 text-muted-foreground text-sm">
          Where will your story begin?
        </p>
      </div>

      {templates === null && fetchError === null && (
        <div className="text-muted-foreground text-sm">Loading scenarios…</div>
      )}

      {fetchError !== null && (
        <div className="text-destructive text-sm">{fetchError}</div>
      )}

      {templates !== null && templates.length === 0 && (
        <div className="text-muted-foreground text-sm">
          No scenarios available — start with a blank world.
        </div>
      )}

      {templates !== null && templates.length > 0 && (
        <div className="flex w-full max-w-sm flex-col gap-3">
          {templates.map((template) => {
            const isSelected = localSelection === template.id;
            const badges = BADGE_OVERRIDES[template.id];
            return (
              <Card
                className={`cursor-pointer transition-colors ${
                  isSelected ? "border-primary" : "hover:border-primary/50"
                }`}
                key={template.id}
                onClick={() => setLocalSelection(template.id)}
              >
                <CardHeader>
                  <div className="mb-1 text-muted-foreground text-xs">
                    {renderRuleset(template.ruleset)}
                  </div>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                    {template.description}
                  </p>
                  {badges !== undefined && badges.length > 0 && (
                    <div className="mt-3 flex gap-3 text-muted-foreground text-xs">
                      {badges.map((badge) => (
                        <span key={badge}>{badge}</span>
                      ))}
                    </div>
                  )}
                </CardHeader>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex flex-col items-center gap-3">
        <div className="flex gap-3">
          <Button onClick={onBack} type="button" variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            className="w-36"
            disabled={localSelection === null}
            onClick={() => {
              if (localSelection) {
                onNext(localSelection);
              }
            }}
            type="button"
          >
            Select
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <button
          className="text-muted-foreground text-sm underline"
          onClick={onSkip}
          type="button"
        >
          Skip — start with a blank world
        </button>
      </div>
    </div>
  );
}
